// PR 0 — BattleScene mixin extraction (structural-only).
//
// Cinematic / lifecycle methods extracted from BattleScene to keep that file
// readable as PRs 1–3 add more event-handler surface area. Zero behavior
// change: each function is invoked via `.call(this, ...)` from a one-line
// delegator on BattleScene, preserving the original `this`-binding semantics.
//
// `BattleScene` is imported type-only to avoid a runtime circular dependency
// (BattleScene.ts imports this module as a value).
import type Phaser from 'phaser';
import type { BattleScene } from './BattleScene';
import { effectsReduced } from '../render/motion';

/** Build + render the cinematic-hero text overlay ("VICTORY" / "BLACKOUT").
 *  DM Sans 44px/800 — the "Cinematic hero" row added to DESIGN.md 2026-
 *  05-13 specifically to host this beat (28px H1 is undersized; 56px
 *  wordmark too large mid-canvas). Glass-fill-thick backdrop centered
 *  on the canvas. Resolves when the fade-out completes.
 *
 *  Sequence (non-reduced motion):
 *    t=0       insert with opacity 0, scale 0.96
 *    t=240ms   scale 1.0 + opacity 1.0
 *    t=240+holdMs  hold
 *    t=+200ms  fade out
 *    t=+200ms  remove from DOM, resolve
 *  Reduced motion: instant on/off for the holdMs window. */
function playHeroText(text: string, holdMs: number): Promise<void> {
  const parent = document.getElementById('game') ?? document.body;
  const el = document.createElement('div');
  el.className = 'battle-hero-text';
  el.textContent = text;
  Object.assign(el.style, {
    position: 'absolute',
    inset: '0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily:
      '"-apple-system", "SF Pro Display", "DM Sans Variable", "DM Sans", system-ui, sans-serif',
    fontSize: '44px',
    fontWeight: '800',
    letterSpacing: '-0.01em',
    color: 'var(--text-primary)',
    background: 'var(--glass-fill-thick)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    pointerEvents: 'none',
    zIndex: '950',
    opacity: '0',
    transform: 'scale(0.96)',
    transition: 'opacity 240ms ease-out, transform 240ms ease-out',
  });
  parent.appendChild(el);
  return new Promise<void>((resolve) => {
    const reduced = effectsReduced();
    if (reduced) {
      el.style.opacity = '1';
      el.style.transform = 'scale(1)';
      el.style.transition = 'none';
      setTimeout(() => {
        el.remove();
        resolve();
      }, holdMs);
      return;
    }
    // Entry
    requestAnimationFrame(() => {
      el.style.opacity = '1';
      el.style.transform = 'scale(1)';
    });
    // Hold then fade
    setTimeout(() => {
      el.style.transition = 'opacity 200ms ease-out';
      el.style.opacity = '0';
      setTimeout(() => {
        el.remove();
        resolve();
      }, 200);
    }, 240 + holdMs);
  });
}

/** Opponent-death fade — fires on the LAST opponent faint that ends
 *  the battle (p1 win). Replaces the standard tweenFaint with a
 *  desaturate-to-gray + alpha fade so the defeated Pokémon dies
 *  visibly without the scene-wide desat the regular faint sequence
 *  applies. The accompanying VICTORY hero text used to be chained
 *  here, but moved to runVictoryHero — playing it AFTER the EXP loop
 *  makes it the FINAL cathedral beat that flows directly into the
 *  exit fade, rather than dropping it mid-flow and lingering on the
 *  combat scene afterward (user feedback 2026-05-13). */
export async function runOpponentDeathFade(
  this: BattleScene,
  sprite: Phaser.GameObjects.Image,
): Promise<void> {
  if (!sprite.active) return;
  await new Promise<void>((resolve) => {
    sprite.setTint(0x808080);
    this.tweens.add({
      targets: sprite,
      alpha: 0,
      duration: 600,
      ease: 'Cubic.easeIn',
      onComplete: () => resolve(),
    });
  });
}

/** VICTORY hero — DM Sans 44px/800 on glass-fill-thick backdrop.
 *  240ms scale-in entry, 800ms hold, 200ms fade-out per DESIGN.md
 *  "Victory cinematic" spec. Called from BattleScene.battleEnd as
 *  the FINAL beat before exit so the hero fade-out flows directly
 *  into the exit fade-out with no lingering combat scene. */
export function runVictoryHero(this: BattleScene): Promise<void> {
  void this; // kept for symmetry with runOpponentDeathFade's signature
  return playHeroText('VICTORY', 800);
}

/** Defeat cinematic — fires when the LAST player Pokémon faints (full
 *  party wipe). Runs AFTER the per-faint sequence completes ("X has
 *  been lost." dialogue) so the per-Pokémon mourning beat is preserved;
 *  this layers the cathedral world-desat + BLACKOUT hero on top.
 *
 *  Tone note: "BLACKOUT" is cartridge-faithful but reads edgy in 2026
 *  chrome. Validate on real device; alternatives logged in DESIGN.md
 *  motion section: "OUT" / "DOWN" / "OVER" / silence. */
export async function runDefeatCinematic(this: BattleScene): Promise<void> {
  const gameEl = document.getElementById('game');
  gameEl?.classList.add('is-battle-defeat');
  // World desat already cued by is-battle-fainting (saturate 0.6) from
  // the faint sequence; this class composes a deeper dim. CSS supplies
  // the 600ms transition. We wait the same window before the hero text.
  await new Promise<void>((r) => setTimeout(r, 600));
  await playHeroText('BLACKOUT', 800);
  gameEl?.classList.remove('is-battle-defeat');
}

export function tweenAttacker(this: BattleScene, side: 'p1' | 'p2'): Promise<void> {
  const sprite = side === 'p1' ? this.playerSprite : this.opponentSprite;
  const dx = side === 'p1' ? 12 : -12;
  const dy = side === 'p1' ? -12 : 12;
  const ox = sprite.x;
  const oy = sprite.y;
  return new Promise((resolve) => {
    this.tweens.add({
      targets: sprite,
      x: ox + dx,
      y: oy + dy,
      duration: 90,
      yoyo: true,
      ease: 'Quad.easeOut',
      onComplete: () => {
        sprite.setPosition(ox, oy);
        resolve();
      },
    });
  });
}

export function tweenFaint(
  this: BattleScene,
  sprite: Phaser.GameObjects.Image,
): Promise<void> {
  return new Promise((resolve) => {
    this.tweens.add({
      targets: sprite,
      y: sprite.y + 32,
      alpha: 0,
      duration: 600,
      ease: 'Cubic.easeIn',
      onComplete: () => resolve(),
    });
  });
}

/**
 * Player-faint cinematic per design doc rev 6 lines 420-428.
 *
 *   t=0       triggerFaint() + body class added; sprite drop tween
 *             runs concurrently (caller awaits both).
 *   t=0-300   HP bar ::after red overlay pulses 3× (CSS animation).
 *   t=300-900 HP bar ::before type-tint drains 1 → 0 (CSS).
 *   t=600-1200 canvas saturate drops 1.0 → 0.6 (CSS on #game).
 *   t=900     appendLog "{Name} has been lost."
 *   t=2500    remove body class; saturate returns to 1.0 (CSS, 400 ms).
 *
 * Returns when the 2500 ms timed window completes. No user input
 * required — the original dialogueOverlay-based variant hung the
 * sim's event loop when the dialogue's tap-to-dismiss path stalled
 * during a force-switch (user report 2026-05-11: "after my pidgey
 * died ... I hit charmander but nothing happened"). The cinematic's
 * emotional weight comes from the visual beats; the "has been lost"
 * line still lands in the log. Auto-advance also matches what the
 * cartridge does (faint resolves itself; player picks next mon).
 */
export async function runPlayerFaintCinematic(
  this: BattleScene,
  lostName: string,
): Promise<void> {
  this.playerStatusPanel?.triggerFaint();
  this.playerStatusPanel?.setLastAlive(false);
  const gameEl = document.getElementById('game');
  // Atomic swap: if last-alive-critical was active (saturate 0.85),
  // drop it in the same DOM tick we add battle-fainting (saturate
  // 0.6) so the canvas saturation transition is continuous rather
  // than flashing toward saturate(1) between states.
  gameEl?.classList.remove('is-last-alive-critical');
  gameEl?.classList.add('is-battle-fainting');
  await this.wait(900);
  this.appendLog(`${lostName} has been lost.`);
  await this.wait(1600);
  gameEl?.classList.remove('is-battle-fainting');
}

export function exitToWorld(this: BattleScene): void {
  this.cameras.main.fadeOut(180, 253, 244, 220);
  this.cameras.main.once('camerafadeoutcomplete', () => {
    // Stop ourselves and wake WorldScene (which was sleeping, not paused —
    // sleep halts update + render, wake resumes both with state intact).
    this.scene.stop();
    this.scene.wake('WorldScene');
  });
}

/**
 * Phase 12 M5: route to EndRunScene instead of waking the WorldScene.
 * The WorldScene stays asleep — the run is over and the EndRunScene's
 * Reset Run reloads the page, tearing both scenes down naturally. We
 * `scene.stop()` ourselves so when the page reloads, BattleScene
 * isn't left in a half-state. Wrapped in try/catch per the failure-
 * mode plan (M5 critical gap): if EndRunScene launch fails for any
 * reason, fall back to alert + reload so the player can't softlock.
 */
export function exitToEndRun(
  this: BattleScene,
  mode: 'win' | 'defeat',
  casualties: Array<{ species: string; level: number }>,
): void {
  this.cameras.main.fadeOut(180, 253, 244, 220);
  this.cameras.main.once('camerafadeoutcomplete', () => {
    try {
      // Glass-Over-World PR 2: launch FIRST then stop so the
      // SceneManager's FIFO queue runs EndRunScene.init (which
      // transferOwnership's the snapshot off this scene) BEFORE
      // BattleScene's SHUTDOWN fires. If we stopped first the
      // shutdown listener would dispose the DynamicTexture before
      // EndRun could claim it, and EndRun would land on a black
      // backdrop after the gold-fade — exactly the flash we're
      // avoiding.
      this.scene.launch('EndRunScene', {
        mode,
        casualties,
        snapshotHandle: this.params.snapshotHandle ?? null,
      });
      this.scene.stop();
    } catch (err) {
      console.error('[battle] EndRunScene launch failed:', err);
      // Anti-softlock fallback. Better a jarring browser dialog than
      // a frozen post-Brock screen.
      const ok = window.confirm(
        mode === 'win'
          ? 'You defeated Brock! Run complete. Reload to start a fresh run?'
          : 'Your party has fallen. Reload to start a fresh run?',
      );
      if (ok) window.location.reload();
    }
  });
}
